import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/components/layout/DashboardLayout";
import CoachDashboard from "@/pages/coach/CoachDashboard";
import Students from "@/pages/coach/Students";
import StudentDetail from "@/pages/coach/StudentDetail";
import DietPlans from "@/pages/coach/DietPlans";
import TrainingPlans from "@/pages/coach/TrainingPlans";
import CoachCalendar from "@/pages/coach/CoachCalendar";
import CoachMessages from "@/pages/coach/CoachMessages";
import CoachPayments from "@/pages/coach/CoachPayments";
import CoachSubscription from "@/pages/coach/CoachSubscription";
import CoachPackages from "@/pages/coach/CoachPackages";
import SubscriptionSuccess from "@/pages/coach/SubscriptionSuccess";
import StripeConnectReturn from "@/pages/coach/StripeConnectReturn";
import StudentDashboard from "@/pages/student/StudentDashboard";
import StudentToday from "@/pages/student/StudentToday";
import MyDietPlan from "@/pages/student/MyDietPlan";
import MyTrainingPlan from "@/pages/student/MyTrainingPlan";
import Progress from "@/pages/student/Progress";
import Photos from "@/pages/student/Photos";
import StudentMessages from "@/pages/student/StudentMessages";
import NotificationsPage from "@/pages/NotificationsPage";
import StudentPayments from "@/pages/student/StudentPayments";
import StudentSessions from "@/pages/student/StudentSessions";
import AiChat from "@/pages/student/AiChat";
import SupportPage from "@/pages/SupportPage";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminContent from "@/pages/admin/AdminContent";
import AdminCommunications from "@/pages/admin/AdminCommunications";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminInvite from "@/pages/admin/AdminInvite";
import AdminBadges from "@/pages/admin/AdminBadges";
import AdminRegisterPage from "@/pages/AdminRegisterPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PayRequestPage from "@/pages/PayRequestPage";
import MentionsLegales from "@/pages/legal/MentionsLegales";
import PolitiqueConfidentialite from "@/pages/legal/PolitiqueConfidentialite";
import CGU from "@/pages/legal/CGU";
import CookieBanner from "@/components/CookieBanner";
import InstallPWA from "@/components/InstallPWA";
import PublicCoachProfile from "@/pages/PublicCoachProfile";
import CoachProfile from "@/pages/coach/CoachProfile";
import CoachExerciseVideos from "@/pages/coach/CoachExerciseVideos";
import CoachDailyCheckins from "@/pages/coach/CoachDailyCheckins";
import CoachMealLogs from "@/pages/coach/CoachMealLogs";
import Supplements from "@/pages/coach/Supplements";
import ExerciseLibrary from "@/pages/coach/ExerciseLibrary";
import StudentReport from "@/pages/coach/StudentReport";
import CoachSecurity from "@/pages/coach/CoachSecurity";
import CoachAccount from "@/pages/coach/CoachAccount";
import StudentProfile from "@/pages/student/StudentProfile";
import MealLog from "@/pages/student/MealLog";
import MySupplements from "@/pages/student/MySupplements";
import ShoppingList from "@/pages/student/ShoppingList";
import WorkoutLog from "@/pages/student/WorkoutLog";
import MyCoach from "@/pages/student/MyCoach";
import PlanTemplates from "@/pages/coach/PlanTemplates";
import CoachBloodTests from "@/pages/coach/CoachBloodTests";
import BloodTests from "@/pages/student/BloodTests";
import StudentBadges from "@/pages/student/StudentBadges";
import CoachBadges from "@/pages/coach/CoachBadges";
import AuthLoadingScreen from "@/components/AuthLoadingScreen";
import { hasStoredSession } from "@/lib/authStorage";

function DashboardRedirect() {
  const { user, loading } = useAuth();
  if (loading || (!user && hasStoredSession())) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" />;
  if (user.role === 'admin') return <Navigate to="/admin/dashboard" />;
  return <Navigate to={`/${user.role}/dashboard`} />;
}

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google Auth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<DashboardRedirect />} />

      <Route path="/coach" element={<DashboardLayout />}>
        <Route path="dashboard" element={<CoachDashboard />} />
        <Route path="students" element={<Students />} />
        <Route path="students/:id" element={<StudentDetail />} />
        <Route path="students/:id/report" element={<StudentReport />} />
        <Route path="meal-logs" element={<CoachMealLogs />} />
        <Route path="supplements" element={<Supplements />} />
        <Route path="exercise-library" element={<ExerciseLibrary />} />
        <Route path="diet-plans" element={<DietPlans />} />
        <Route path="training-plans" element={<TrainingPlans />} />
        <Route path="calendar" element={<CoachCalendar />} />
        <Route path="messages" element={<CoachMessages />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="payments" element={<CoachPayments />} />
        <Route path="subscription" element={<CoachSubscription />} />
        <Route path="subscription/success" element={<SubscriptionSuccess />} />
        <Route path="packages" element={<CoachPackages />} />
        <Route path="stripe-connect/return" element={<StripeConnectReturn />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="profile" element={<CoachProfile />} />
        <Route path="exercise-videos" element={<CoachExerciseVideos />} />
        <Route path="daily-checkins" element={<CoachDailyCheckins />} />
        <Route path="security" element={<CoachSecurity />} />
        <Route path="account" element={<CoachAccount />} />
        <Route path="plan-templates" element={<PlanTemplates />} />
        <Route path="blood-tests" element={<CoachBloodTests />} />
        <Route path="badges" element={<CoachBadges />} />
      </Route>

      <Route path="/student" element={<DashboardLayout />}>
        <Route path="dashboard" element={<StudentDashboard />} />
        <Route path="today" element={<StudentToday />} />
        <Route path="diet-plan" element={<MyDietPlan />} />
        <Route path="meal-log" element={<MealLog />} />
        <Route path="supplements" element={<MySupplements />} />
        <Route path="shopping-list" element={<ShoppingList />} />
        <Route path="training-plan" element={<MyTrainingPlan />} />
        <Route path="workout-log" element={<WorkoutLog />} />
        <Route path="progress" element={<Progress />} />
        <Route path="photos" element={<Photos />} />
        <Route path="sessions" element={<StudentSessions />} />
        <Route path="messages" element={<StudentMessages />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="ai-chat" element={<AiChat />} />
        <Route path="payments" element={<StudentPayments />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="profile" element={<StudentProfile />} />
        <Route path="my-coach" element={<MyCoach />} />
        <Route path="blood-tests" element={<BloodTests />} />
        <Route path="badges" element={<StudentBadges />} />
      </Route>

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="revenue" element={<AdminRevenue />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="communications" element={<AdminCommunications />} />
        <Route path="system" element={<AdminSystem />} />
        <Route path="invitations" element={<AdminInvite />} />
        <Route path="badges" element={<AdminBadges />} />
      </Route>
      <Route path="/admin-register" element={<AdminRegisterPage />} />

      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
      <Route path="/cgu" element={<CGU />} />

      <Route path="/pay/:token" element={<PayRequestPage />} />

      <Route path="/@:pseudo" element={<PublicCoachProfile />} />
      <Route path="/:slug" element={<PublicCoachProfile />} />

      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<Navigate to="/student/payments" />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <AppRouter />
        <CookieBanner />
        <InstallPWA />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
